import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ExternalLink as ExternalLinkIcon } from 'lucide-react';

import type { SiteDetailContext } from './SiteRoute';
import { SITE_STATUS } from '../constants/siteStatus';
import { useSiteActions } from '../hooks/useSiteActions';
import { useDomains } from '../hooks/useDomains';
import { useDetachDomain } from '../hooks/useDetachDomain';
import { useConfirm } from '@/contexts/ConfirmContext';
import { useToast } from '@/contexts/ToastContext';
import { useAppCache } from '@/contexts/AppCacheContext';
import { formatPrice, formatPriceCta, formatPriceWithSchedule } from '../utils/formatPrice';
import { Time } from '@/components/ui/time';

import { APP_CONFIG } from '../config/app.config';
import { SiteDomainManager } from '../components/sites/SiteDomainManager';
import { SiteQRCode } from '../components/sites/SiteQRCode';
import { SiteDomainStatusCard } from '../components/sites/SiteDomainStatusCard';
import { SiteDetailAnalytics } from './SiteDetailAnalytics';
import { SiteDetailEvents } from './SiteDetailEvents';

import { LoadingButton } from '@/components/ui/loading-button';
import { ExternalLink } from '@/components/ui/external-link';

/**
 * Overview tab body. Owns subscription summary, URL/QR/template/dates,
 * domain status card, and domain manager. Header chrome + delete dialog
 * live in `SiteRoute`. Analytics lives in `SiteDetailAnalytics`.
 */

export const SiteDetailOverview: React.FC = () => {
  const { site, refetch, subdomain, domain, hasActiveSub, monthlyPrice } =
    useOutletContext<SiteDetailContext>();
  const { hasPermission } = useAppCache();
  const confirm = useConfirm();
  const toast = useToast();
  const detachDomain = useDetachDomain();

  const canReadDomains = hasPermission('read:domains');
  const canManageBilling = hasPermission('manage:billing');

  const [attachDomainModalOpen, setAttachDomainModalOpen] = useState(false);

  const handleDisconnect = async (domainId: string) => {
    const confirmed = await confirm({
      title: 'Disconnect domain',
      description:
        'Disconnect this domain from the site? The domain will remain in your account.',
      confirmText: 'Disconnect',
      cancelText: 'Cancel',
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await detachDomain.mutateAsync({ domainId, siteId: site.id });
      toast.success('Domain disconnected from this site.', 'Disconnected');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to disconnect domain', 'Disconnect failed');
    }
  };

  // Pull a single page large enough to cover an org's domain set; the
  // overview filters down to this site's custom domains locally. Mutations
  // elsewhere (attach, detach, create, delete) invalidate `['domains']` so
  // this view stays fresh.
  const {
    data: domainsPage,
    isLoading: domainsQueryLoading,
    error: domainsQueryError,
  } = useDomains({ perPage: 200 });

  const customDomains = (domainsPage?.items ?? []).filter((d) => d.type === 'custom');
  const attachedCustomDomains = canReadDomains
    ? customDomains.filter((d) => d.siteId === site.id)
    : [];
  const availableVerifiedDomains = canReadDomains
    ? customDomains.filter((d) => d.verified && !d.siteId)
    : [];
  // If the user can't read domains, treat the section as resolved (empty)
  // so the manager doesn't sit on a "Loading…" row forever.
  const domainsLoading = canReadDomains ? domainsQueryLoading : false;
  const domainsError = domainsQueryError
    ? domainsQueryError instanceof Error
      ? domainsQueryError.message
      : 'Failed to load domains'
    : null;

  const {
    handleUpgrade,
    handleCancelSubscription,
    handleReactivateSubscription,
    isCancellingSubscription,
    isReactivatingSubscription,
  } = useSiteActions(site.id, {
    onSubscriptionChange: () => refetch(),
    onPublished: () => refetch(),
  });

  // ---- Derived state -----------------------------------------------------

  const tier = (site.template?.tier as 'standard' | 'premium' | undefined) ?? 'standard';

  /**
   * One-line subscription summary rendered as the first KV row in Overview.
   * Driven by subscription state (not site lifecycle status), so it renders
   * correctly during transitional states (publishing/destroying/failed) too.
   */
  const subscriptionSummary = (): string => {
    const periodEnd = site.subscriptionCurrentPeriodEnd;

    // Suspended sites get a countdown: 30 days until auto-offline sweep.
    if (site.status === SITE_STATUS.SUSPENDED) {
      const suspendedAt = site.suspendedAt;
      if (suspendedAt) {
        const offlineAt = new Date(suspendedAt).getTime() + 30 * 24 * 60 * 60 * 1000;
        const daysLeft = Math.max(0, Math.ceil((offlineAt - Date.now()) / (24 * 60 * 60 * 1000)));
        return `Suspended — offline in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`;
      }
      return 'Suspended';
    }

    if (hasActiveSub) {
      if (site.subscriptionStatus === 'past_due') return 'Past due';
      return formatPriceWithSchedule(monthlyPrice, {
        periodEnd,
        cancelling: !!site.subscriptionCancelAtPeriodEnd,
      });
    }

    // No active sub — free path. Disambiguate the messaging by lifecycle state.
    if (site.paymentRequired) return `${formatPrice(monthlyPrice)} on publish`;
    if (site.status === SITE_STATUS.DRAFT) return 'Free — first site';
    return 'Free';
  };

  const subSummary = subscriptionSummary();

  return (
    // Single-page overview: Analytics card on top, then a 60/40 split with
    // Overview KV → Domains → Activity log on the left and the "Next steps"
    // sidebar on the right. Tab routing is preserved (App.tsx still wires
    // /overview, /activity, /analytics) but the tab bar is hidden in
    // SiteRoute while we evaluate this layout.
    <div className="space-y-12">
      {/* Analytics — pinned above the 2-col layout. */}
      <SiteDetailAnalytics />

      <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-8">
      {/* Left column (60%) — Overview KV → Domains → Activity log */}
      <div className="space-y-12 min-w-0">

      {/* Site Overview */}
      <section className="space-y-4">
        <h2 className="text-xl font-medium text-foreground">Overview</h2>
        <div className="min-w-0">
          <dl className="space-y-4">
              {/* Subscription — summary on one line; transition CTA on its
                  own row below, indented under the value column.
                  Upgrade redirects to Stripe Checkout. Cancel and Reactivate
                  are in-app actions calling Stripe via our backend. */}
              <div className="flex items-baseline">
                <dt className="text-sm font-medium text-muted-foreground w-28 flex-shrink-0">Subscription</dt>
                <dd className="text-sm text-foreground">
                  <div>{subSummary}</div>
                  <div className="mt-2">
                    {canManageBilling && !hasActiveSub && (
                      <LoadingButton
                        variant="tertiary"
                        size="xs"
                        onClick={() => { void handleUpgrade(); }}
                        loading={false}
                      >
                        Upgrade subscription{formatPriceCta(monthlyPrice)}
                        <ExternalLinkIcon className="h-3.5 w-3.5 ml-1 stroke-[1.5]" />
                      </LoadingButton>
                    )}
                    {canManageBilling && hasActiveSub && !site.subscriptionCancelAtPeriodEnd && (
                      <LoadingButton
                        variant="tertiary-destructive"
                        size="xs"
                        onClick={handleCancelSubscription}
                        loading={isCancellingSubscription}
                        loadingText="Cancelling…"
                      >
                        Cancel subscription
                        <ExternalLinkIcon className="h-3.5 w-3.5 ml-1 stroke-[1.5]" />
                      </LoadingButton>
                    )}
                    {canManageBilling && hasActiveSub && site.subscriptionCancelAtPeriodEnd && (
                      <LoadingButton
                        variant="tertiary"
                        size="xs"
                        onClick={handleReactivateSubscription}
                        loading={isReactivatingSubscription}
                        loadingText="Reactivating…"
                      >
                        Reactivate subscription
                        <ExternalLinkIcon className="h-3.5 w-3.5 ml-1 stroke-[1.5]" />
                      </LoadingButton>
                    )}
                  </div>
                </dd>
              </div>

              {/* URL */}
              <div className="flex items-baseline">
                <dt className="text-sm font-medium text-muted-foreground w-28 flex-shrink-0">URL</dt>
                <dd className="space-y-1">
                  {subdomain && (
                    <div>
                      <ExternalLink
                        href={`https://${subdomain}.${APP_CONFIG.SITES_DOMAIN}`}
                        className="text-sm"
                      >
                        {subdomain}.{APP_CONFIG.SITES_DOMAIN}
                      </ExternalLink>
                    </div>
                  )}
                  {domain && (
                    <div>
                      <ExternalLink
                        href={`https://${domain}`}
                        className="text-sm"
                      >
                        {domain}
                      </ExternalLink>
                    </div>
                  )}
                </dd>
              </div>

              {/* QR code */}
              <div className="flex items-baseline">
                <dt className="text-sm font-medium text-muted-foreground w-28 flex-shrink-0">QR code</dt>
                <dd className="text-sm text-foreground">
                  <SiteQRCode
                    subdomain={subdomain}
                    customDomains={domain ? [domain] : []}
                  />
                </dd>
              </div>

              {/* Template */}
              <div className="flex items-baseline">
                <dt className="text-sm font-medium text-muted-foreground w-28 flex-shrink-0">Template</dt>
                <dd className="text-sm text-foreground">{site.template?.displayName || 'Unknown'}</dd>
              </div>

              {/* Tier */}
              <div className="flex items-baseline">
                <dt className="text-sm font-medium text-muted-foreground w-28 flex-shrink-0">Tier</dt>
                <dd className="text-sm text-foreground capitalize">{tier}</dd>
              </div>

            {/* Created */}
            <div className="flex items-baseline">
              <dt className="text-sm font-medium text-muted-foreground w-28 flex-shrink-0">Created</dt>
              <dd className="text-sm text-foreground"><Time value={site.createdAt} format="medium" /></dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Domain Management — heading rendered inline with the action
          buttons via SiteDomainManager's headerTitle prop. */}
      <section>
        <SiteDomainManager
          siteId={site.id}
          subdomain={subdomain}
          isPaid={hasActiveSub}
          onUpgrade={() => { void handleUpgrade(); }}
          attachedCustomDomains={attachedCustomDomains}
          domainsLoading={domainsLoading}
          domainsError={domainsError}
          attachDomainModalOpen={attachDomainModalOpen}
          onAttachDomainModalChange={setAttachDomainModalOpen}
          headerTitle="Domains"
        />
      </section>

      {/* Activity log — pulled inline from the (still-routed) /activity
          tab so the entire site detail reads on a single page. */}
      <SiteDetailEvents />
      </div>

      {/* Right column (40%) — "Next steps" sidebar with the
          Connect-your-domain CTA card. Card returns null once a custom
          domain is attached. */}
      <aside className="space-y-4 min-w-0">
        <h2 className="text-xl font-medium text-foreground">Next steps</h2>
        <SiteDomainStatusCard
          hasActiveSub={hasActiveSub}
          attachedCustomDomains={attachedCustomDomains}
          availableVerifiedDomains={availableVerifiedDomains}
          canReadDomains={canReadDomains}
          onUpgrade={() => { void handleUpgrade(); }}
          onConnectClick={() => setAttachDomainModalOpen(true)}
          onDisconnect={(domainId) => { void handleDisconnect(domainId); }}
          isDisconnecting={detachDomain.isPending}
        />
      </aside>
      </div>
    </div>
  );
};

export default SiteDetailOverview;
